import React, {useEffect, useState} from 'react';
import {Badge, Card, Dropdown, Pagination, Table} from 'react-bootstrap';

export interface Column<T> {
    label: string;
    varName: keyof T;
    render?: (item: T) => React.ReactNode;
    sortable?: boolean;
}

interface TableWithActionsDropdownProps<T> {
    // if using lazy loading, data is only current page. otherwise it is all data.
    data: T[];
    totalCount?: number;
    columns: Column<T>[];
    pageSize?: number;
    actions?: (item: T) => React.ReactNode;
    headerHTML?: () => React.ReactNode;
    onPageChange?: (newPage: number, pageSize: number, sortField: string, sortByDesc: boolean) => void;
    defaultSortDesc?: boolean;
    defaultSortField?: string; // should be a column var name in the columns array
}

function TableWithActionsDropdown<T extends Record<string, any>>(
    {
        data,
        totalCount,
        defaultSortField,
        columns,
        pageSize = 10,
        actions,
        headerHTML,
        onPageChange,
        defaultSortDesc = false,
    }: TableWithActionsDropdownProps<T>) {
    const [sortField, setSortField] = useState<keyof T>(defaultSortField ?? columns[0].varName);
    const [sortDesc, setSortDesc] = useState(defaultSortDesc);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (onPageChange) {
            onPageChange(currentPage - 1, pageSize, sortField.toString(), sortDesc);
        }
    }, [currentPage, sortField, sortDesc, pageSize]);

    // only if not lazy loading. sorts client side
    const sorted = onPageChange
        ? data
        : [...data].sort((a, b) => {
            const aVal = a[sortField], bVal = b[sortField];
            if (aVal == null || bVal == null) return 0;
            if (aVal < bVal) return sortDesc ? 1 : -1;
            if (aVal > bVal) return sortDesc ? -1 : 1;
            return 0;
        });

    const totalRows = totalCount ?? sorted.length;
    const totalPages = Math.ceil(totalRows / pageSize);

    const start = (currentPage - 1) * pageSize;
    const pageData = onPageChange ? data : sorted.slice(start, start + pageSize);

    useEffect(() => {
        if(onPageChange)
            onPageChange(currentPage - 1, pageSize, sortField.toString(), sortDesc);
    }, []);

    const handlePage = (page: number) => {
        setCurrentPage(page);
        onPageChange(page, pageSize, sortField.toString(), sortDesc);
    };

    const handleSort = (field: keyof T, sortable = true) => {
        if (!sortable) return;
        const newDesc = field === sortField ? !sortDesc : defaultSortDesc;
        setSortField(field);
        setSortDesc(newDesc);
        setCurrentPage(1);
        if(onPageChange)
            onPageChange(0, pageSize, field.toString(), sortDesc);
    };

    return (
        <>
            <div className="d-flex align-items-end justify-content-between mt-2 mb-3">
                <>
                    <div className="fs-5 fst-italic">
                        viewing {pageData.length} of {totalRows}
                    </div>
                    {headerHTML && headerHTML()}
                </>
            </div>
            <Card className="border no-shadow">
                <Card.Body className="p-0">
                    <Table hover striped className="align-middle m-0">
                        <thead>
                        <tr>
                            {columns.map(({label, varName, sortable = true}) => (
                                <th key={String(varName)} onClick={() => handleSort(varName, sortable)} style={{cursor: sortable ? 'pointer' : 'default'}}>
                                    <a href="#" className="sort-icons" onClick={(e) => e.preventDefault()}>
                                        {sortable && (
                                            <>
                                                <span className={`icon ${sortField === varName && !sortDesc ? 'active' : ''}`}>
                                                    <i className="far fa-long-arrow-alt-up" />
                                                </span>
                                                <span className={`icon ${sortField === varName && sortDesc ? 'active' : ''}`}>
                                                    <i className="far fa-long-arrow-alt-down" />
                                                </span>
                                            </>
                                        )}
                                    </a>
                                    {label}
                                </th>
                            ))}
                            {actions && <th className="text-end">Actions</th>}
                        </tr>
                        </thead>
                        <tbody>
                            {pageData.map((item, idx) => (
                                <tr key={idx} className={(item as any).inactive ? 'nullRow' : ''}>
                                    {columns.map(({varName, render}, i) => (
                                    <td key={i}>{render ? render(item) : String(item[varName] ?? '')}</td>
                                    ))}
                                    {actions && (
                                    <td className="text-end">
                                    <Dropdown style={{display: 'flex', justifyContent: 'flex-end'}} align="end" className="text-end">
                                        <Dropdown.Toggle variant="outline-dark" size="lg" className="action-btn text-end">
                                            <i className="far fa-ellipsis-v"></i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {actions(item)}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
            
            {totalPages > 1 && (
            <div className="row mt-4">
                <nav aria-label="table navigation" className="d-flex align-items-center justify-content-center">
                    <Pagination className="mb-0">
                        <Pagination.Prev onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)}/>
                        {Array.from({length: totalPages}, (_, i) => (
                            <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => handlePage(i+1)}>
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => currentPage < totalPages && setCurrentPage((p) => p + 1)}/>
                    </Pagination>
                </nav>
            </div>
            )}
        </>
    );
}

export default TableWithActionsDropdown;
